import { effectScope, watch } from 'vue'
import type { Directive } from 'vue'
import { useDeviceManager } from '../stores/deviceManager'
import { isSpeakerSelectionSupported, setSpeakerDevice } from '../utils/mediaDevicePrefs'

/**
 * Директива `v-audio-sink` — единый механизм маршрутизации вывода звука.
 *
 * Любой звучащий `<audio>`/`<video>` помечается `v-audio-sink` и автоматически
 * попадает под управление: выбранный динамик применяется ко всем таким элементам
 * разом при смене устройства и точечно при монтировании нового элемента.
 *
 * Устройство берётся напрямую из Pinia-store (единый источник истины), а не через
 * inject: хуки директивы — не setup компонента, надёжного inject-контекста у них нет.
 */

// Все звучащие элементы под директивой — общий реестр, чтобы смена
// выбранного динамика прогонялась сразу по всем.
const elements = new Set<HTMLMediaElement>()

// Общий watcher заводится лениво при монтировании первого элемента и живёт
// до конца жизни приложения — снимать его незачем.
let watcherStarted = false

// Единственная точка применения к одному элементу — общая обёртка
// setSpeakerDevice; второй путь к setSinkId не заводим. Сбой применения
// (в т.ч. Safari/Firefox без setSinkId) тихо игнорируем — это не ошибка.
async function applyOne(el: HTMLMediaElement, deviceId: string): Promise<void> {
  await setSpeakerDevice(el, deviceId)
}

async function applyToAll(deviceId: string): Promise<void> {
  if (!isSpeakerSelectionSupported()) return
  if (!deviceId) return

  // Применяем ко всем элементам параллельно: один зависший/медленный setSinkId
  // не должен тормозить остальные.
  await Promise.allSettled(
    [...elements].map(el => setSpeakerDevice(el, deviceId))
  )
}

function ensureWatcher(): void {
  if (watcherStarted) return
  watcherStarted = true
  const deviceManager = useDeviceManager()
  // Подписка осознанно глобальная — живёт всё время работы приложения. Detached
  // scope фиксирует её независимость от scope компонента, чей mounted нас позвал:
  // время жизни watcher'а становится детерминированным, а не зависит от того, что
  // хук выполняется вне активного effect scope.
  effectScope(true).run(() => {
    watch(
      () => deviceManager.selectedSpeaker,
      deviceId => { void applyToAll(deviceId) }
    )
  })
}

export const vAudioSink: Directive<HTMLMediaElement> = {
  mounted(el) {
    elements.add(el)
    ensureWatcher()

    // Элемент мог смонтироваться уже после выбора устройства — применяем
    // текущий выбор сразу. На Safari/Firefox тихо выходим.
    if (!isSpeakerSelectionSupported()) return
    const deviceId = useDeviceManager().selectedSpeaker
    if (deviceId) {
      void applyOne(el, deviceId)
    }
  },
  unmounted(el) {
    elements.delete(el)
  },
}
